import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient'; // Make sure this path is correct

const AuthContext = createContext();

const REDIRECT_URL = process.env.REACT_APP_SUPABASE_REDIRECT_URL;

// Include id_token + code/state because some providers include these in callbacks
const AUTH_PARAM_KEYS = [
  'access_token',
  'refresh_token',
  'id_token',
  'expires_at',
  'expires_in',
  'token_type',
  'type',
  'code',
  'state',
  'error',
  'error_description',
];

const getRedirectUrl = () => {
  if (REDIRECT_URL) return REDIRECT_URL;
  if (typeof window !== 'undefined') return `${window.location.origin}/auth/callback`;
  return undefined;
};

const getSessionFromUrl = () => {
  if (typeof window === 'undefined') return null;

  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const searchParams = new URLSearchParams(window.location.search);
  const params = new URLSearchParams();

  hashParams.forEach((value, key) => params.set(key, value));
  searchParams.forEach((value, key) => {
    if (!params.has(key)) params.set(key, value);
  });

  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');

  if (!accessToken || !refreshToken) return null;
  return { accessToken, refreshToken };
};

const clearAuthParamsFromUrl = () => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);

  AUTH_PARAM_KEYS.forEach((key) => url.searchParams.delete(key));

  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
  AUTH_PARAM_KEYS.forEach((key) => hashParams.delete(key));

  const newHash = hashParams.toString();
  const newSearch = url.searchParams.toString();
  const cleaned = `${url.origin}${url.pathname}${newSearch ? `?${newSearch}` : ''}${newHash ? `#${newHash}` : ''}`;

  window.history.replaceState({}, document.title, cleaned);
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const supabaseAvailable = Boolean(supabase);

  useEffect(() => {
    if (!supabaseAvailable) {
      setLoading(false);
      return undefined;
    }

    let isMounted = true;

    const initAuth = async () => {
      try {
        // 1) Restore session from URL params if present (email links / oauth implicit)
        const urlSession = getSessionFromUrl();
        if (urlSession) {
          const { error } = await supabase.auth.setSession({
            access_token: urlSession.accessToken,
            refresh_token: urlSession.refreshToken,
          });

          if (error) {
            console.error('Failed to restore Supabase session from URL:', error);
          }

          // Remove tokens from URL so refresh doesn't re-trigger callback pages
          clearAuthParamsFromUrl();
        }

        // 2) Load current session
        const { data } = await supabase.auth.getSession();
        const session = data?.session;

        if (isMounted) {
          setUser(session?.user ?? null);
          setLoading(false);
        }
      } catch (e) {
        console.error('[AuthContext] initAuth error:', e);
        if (isMounted) setLoading(false);
      }
    };

    initAuth();

    // 3) Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (isMounted) {
        setUser(session?.user ?? null);
      }
    });

    return () => {
      isMounted = false;
      if (subscription) subscription.unsubscribe();
    };
  }, [supabaseAvailable]);

  const value = {
    signUp: (email, password, firstName, lastName) => {
      if (!supabaseAvailable) {
        return Promise.reject(new Error('Supabase is not configured'));
      }

      const redirectTo = getRedirectUrl();
      const options = {
        data: {
          first_name: firstName,
          last_name: lastName,
          full_name: `${firstName} ${lastName}`,
        },
      };

      if (redirectTo) options.emailRedirectTo = redirectTo;

      return supabase.auth.signUp({ email, password, options });
    },

    login: (email, password) => {
      if (!supabaseAvailable) {
        return Promise.reject(new Error('Supabase is not configured'));
      }
      return supabase.auth.signInWithPassword({ email, password });
    },

    logout: async () => {
      if (!supabaseAvailable) return;

      try {
        await supabase.auth.signOut();
      } finally {
        // ✅ IMPORTANT:
        // Always navigate away from /auth/callback after logout
        // so you don't land on callback without params.
        if (typeof window !== 'undefined') {
          clearAuthParamsFromUrl();
          window.location.replace('/');
        }
      }
    },

    user,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
